<template>
  <div v-if="!hasError" id="capital-overview-container" class="w-100" />
</template>

<script setup lang="ts">
import {
  AdyenPlatformExperience,
  CapitalOverview,
  all_locales,
} from "@adyen/adyen-platform-experience-web";
import "@adyen/adyen-platform-experience-web/adyen-platform-experience-web.css";
import { PostHog } from "posthog-js";
import { inject, onMounted, ref } from "vue";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { i18n } from "@/plugins/i18n";
import { useMerchantsStore } from "@/store/merchants";

const { api, currentMerchantId } = useYetipayApi();
const merchantStore = useMerchantsStore();
const posthog = inject<PostHog>("posthog");

const isCapitalEnabled = () => {
  const capEnabled =
    merchantStore.currentMerchant?.accountHolderCapabilities?.["getGrantOffers"]
      ?.verificationStatus === "valid";
  return posthog?.isFeatureEnabled("capital-enabled") && capEnabled;
};

const hasError = ref<boolean>(false);

const localeCountryMap: Record<string, "en-US" | "es-ES" | "fr-FR" | "it-IT"> = {
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  it: "it-IT",
};

const { alwaysRender = false } = defineProps<{
  alwaysRender?: boolean;
}>();

const emit = defineEmits<{
  (e: "mount"): void;
}>();

onMounted(async () => {
  if (!isCapitalEnabled()) {
    return;
  }
  // @ts-expect-error: i18n.global.locale.value is not typed
  const currentLocale = i18n.global.locale.value;
  try {
    const core = await AdyenPlatformExperience({
      onError: (error) => {
        console.error("Error initializing Capital Overview", error);
      },
      availableTranslations: [all_locales],
      environment: import.meta.env.VITE_ENVIRONMENT === "production" ? "live" : "test",
      locale: localeCountryMap[currentLocale] ?? "en-US",
      onSessionCreate: async () => {
        try {
          const res = await api.createCapitalSession(currentMerchantId);
          return {
            id: res.data.id!,
            token: res.data.token!,
          };
        } catch (error) {
          console.error("Error creating Capital session", error);
          hasError.value = true;
          throw error;
        }
      },
    });
    const capitalOverview = new CapitalOverview({
      core,
      skipPreQualifiedIntro: false,
    });

    const { state } = await capitalOverview.getState();
    if (state === "isPreQualified" || state === "hasRequestedGrants" || alwaysRender) {
      capitalOverview.mount("#capital-overview-container");
      emit("mount");
    }
  } catch (error) {
    console.error("Error initializing Capital Overview", error);
    hasError.value = true;
  }
});
</script>

<style>
:root {
  --adyen-sdk-border-radius-s: 10px;
  --adyen-sdk-border-radius-m: 12px;
  --adyen-sdk-border-radius-l: 30px;
  --adyen-sdk-color-background-inverse-primary: rgb(var(--v-theme-purple-4));
  --adyen-sdk-color-background-inverse-primary-hover: rgb(var(--v-theme-purple-4));
  --adyen-sdk-color-decorative-blue: rgb(var(--v-theme-purple-4)); /*ok*/
  --adyen-sdk-color-outline-primary-active: rgb(var(--v-theme-purple-4)); /*buttons*/
  --adyen-sdk-color-background-highlight-strong: rgb(var(--v-theme-purple-4)); /*offer slider*/
  --adyen-sdk-color-background-selected: rgb(var(--v-theme-pink-5)); /*offer slider*/
  --adyen-sdk-color-label-highlight: rgb(var(--v-theme-purple-4)); /*offer amount*/
  --adyen-sdk-color-label-primary: rgb(var(--v-theme-black));
  --adyen-sdk-color-background-highlight-weak: rgb(
    var(--v-theme-pink-5)
  ); /*offer waiting to be approved*/

  .adyen-pe-capital-offer-component,
  .adyen-pe-capital-overview-component {
    border-radius: 24px;
  }
  .adyen-pe-capital-offer-selection__loading-skeleton {
    background: rgb(var(--v-theme-purple-9)) !important; /*offer skeleton*/
  }
}
</style>
